import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, CheckCircle2, Image as ImageIcon } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import styles from '@/pages/admin/ExerciseImport/ExerciseImport.module.css';

const keyOf = (c) => c.externalId || c.name;

/**
 * Importador de equipamentos: candidatos agregados da fonte de exercícios,
 * com imagem (Wikimedia), nome/descrição em pt via IA e traduções en/es.
 */
export default function EquipmentImport() {
  const { t } = useTranslation();
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [jobKeys, setJobKeys] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const importing = job?.status === 'running';

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/admin/equipments/search');
      setCandidates((r.data || r).candidates || []);
    } catch (e) {
      setError(e.message || t('import.errSearchFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api
      .get('/admin/equipments/import/jobs/active')
      .then((r) => {
        const active = (r.data || r).job;
        if (active?.status === 'running') setJob(active);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling do job em andamento.
  useEffect(() => {
    if (!importing) return;
    const timer = setInterval(async () => {
      try {
        const r = await api.get(`/admin/equipments/import/jobs/${job.id}`);
        const next = (r.data || r).job;
        setJob(next);
        if (next.status !== 'running') {
          setResult(next);
          if (jobKeys) {
            setCandidates((list) =>
              list.map((c) => (jobKeys.has(keyOf(c)) ? { ...c, imported: true } : c))
            );
          } else {
            load();
          }
        }
      } catch {
        // transitório: mantém o polling
      }
    }, 1500);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, importing]);

  const toggle = (c) => {
    const k = keyOf(c);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };
  const allSelected = candidates.length > 0 && selected.size === candidates.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(candidates.map(keyOf)));

  const selectedCandidates = useMemo(
    () => candidates.filter((c) => selected.has(keyOf(c))),
    [candidates, selected]
  );

  const startImport = async (items) => {
    if (!items.length || importing) return;
    setError('');
    setResult(null);
    try {
      const res = await api.post('/admin/equipments/import', { candidates: items });
      setJobKeys(new Set(items.map(keyOf)));
      setJob((res.data || res).job);
      setSelected(new Set());
    } catch (e) {
      setError(e.message || t('import.errImportFailed'));
    }
  };

  return (
    <>
      <PageHeader kicker="Movez · Catálogo" title={t('equipments.importTitle')} />

      {error && <div className={styles.error}>{error}</div>}

      {importing && (
        <Card className={styles.progressCard}>
          <div className={styles.progressHead}>
            <Spinner size={16} />
            <strong>{t('import.progressTitle')}</strong>
            <span className={styles.progressNums}>
              {t('import.progressCount', { processed: job.processed, total: job.total })}
            </span>
          </div>
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${job.total ? Math.round((job.processed / job.total) * 100) : 0}%` }}
            />
          </div>
          <div className={styles.progressMeta}>
            {t('import.histCreated', { count: job.created })} · {t('import.histUpdated', { count: job.updated })} ·{' '}
            {t('import.histEnriched', { count: job.enriched })}
            {job.failed ? ` · ${t('import.histFailed', { count: job.failed })}` : ''}
          </div>
        </Card>
      )}

      {result && !importing && (
        <div className={styles.result}>
          <CheckCircle2 size={16} strokeWidth={2} />
          {t('import.resultPrefix')} <strong>{result.created}</strong> {t('import.resultCreated')} ·{' '}
          <strong>{result.updated}</strong> {t('import.resultUpdated')} · <strong>{result.enriched}</strong>{' '}
          {t('import.resultEnriched')} · <strong>{result.translated ?? 0}</strong> {t('import.resultTranslated')}
          {result.failed ? ` · ${t('import.histFailed', { count: result.failed })}` : ''}
        </div>
      )}

      {loading ? (
        <div className={styles.center}>
          <Spinner />
        </div>
      ) : candidates.length === 0 ? (
        <EmptyState title={t('import.emptyTitle')} description={t('import.emptyDescription')} />
      ) : (
        <>
          <div className={styles.selectBar}>
            <button type="button" className={styles.linkBtn} onClick={toggleAll}>
              {allSelected ? t('import.clearSelection') : t('import.selectAll')}
            </button>
            <span style={{ flex: 1 }} />
            <Button variant="outline" onClick={() => startImport(candidates)} disabled={importing}>
              {t('import.importAllCount', { count: candidates.length })}
            </Button>
            <Button onClick={() => startImport(selectedCandidates)} loading={importing} disabled={!selected.size}>
              <Download size={15} strokeWidth={2} />
              {t('import.importSelected', { count: selected.size })}
            </Button>
          </div>

          <div className={styles.grid}>
            {candidates.map((c) => {
              const on = selected.has(keyOf(c));
              return (
                <button
                  type="button"
                  key={keyOf(c)}
                  className={`${styles.card} ${on ? styles.cardOn : ''} ${c.imported ? styles.cardImported : ''}`}
                  onClick={() => toggle(c)}
                >
                  <div className={styles.media}>
                    {c.imageUrl ? (
                      <img src={c.imageUrl} alt={c.name} loading="lazy" decoding="async" />
                    ) : (
                      <ImageIcon size={22} strokeWidth={1.5} />
                    )}
                    {c.imported && !on && (
                      <span className={styles.importedTag}>{t('import.importedBadge')}</span>
                    )}
                    {on && (
                      <span className={styles.check}>
                        <CheckCircle2 size={20} strokeWidth={2.5} />
                      </span>
                    )}
                  </div>
                  <div className={styles.cardBody}>
                    <span className={styles.name}>{c.name}</span>
                    <span className={styles.meta}>
                      {[c.category, t('equipments.usedIn', { count: c.exerciseCount })].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}
